import React from "react";
import PropTypes from "prop-types";
import logoImg from "../../assets/images/Neo_Logo_A.svg";

/**
 * NeoLoader
 * Componente unificado e premium de carregamento do NeoApp no padrão outonal.
 * Suporta cinco modos principais:
 *  - 'inline': Apenas o spinner circular (tabelas, botões ou blocos de texto).
 *  - 'overlay': Cobre o contêiner-pai com blur outonal e o spinner circular centralizado.
 *  - 'full-page': Cobre a tela inteira com a logo pulsante e o spinner.
 *  - 'dots': Três bolinhas pulsantes em onda (bouncing dots) ideal para inline/chat/digitação.
 *  - 'linear': Uma barra de progresso horizontal fluida ideal para o topo de cards ou tabelas.
 */
const NeoLoader = ({
    type = "inline", // 'inline' | 'overlay' | 'full-page' | 'dots' | 'linear'
    size = "md", // 'sm' | 'md' | 'lg'
    variant = "accent", // 'accent' (laranja) | 'primary' (chocolate) | 'white' (branco)
    label = "", // Texto opcional de apoio
    loading = true,
    className = "",
    style = {},
    ...props
}) => {
    if (!loading) return null;

    // 1. Modo Bouncing Dots (Bolinhas ondulantes)
    if (type === "dots") {
        return (
            <div className={`neo-loader-dots neo-loader-dots-${variant} ${className}`} style={style} {...props}>
                <span className="neo-loader-dot"></span>
                <span className="neo-loader-dot"></span>
                <span className="neo-loader-dot"></span>
                {label && <span className="neo-loader-label font-size-13 ms-2">{label}</span>}
            </div>
        );
    }

    // 2. Modo Linear Progress (Barra horizontal fluida)
    if (type === "linear") {
        return (
            <div className={`neo-loader-linear-wrapper ${className}`} style={style} {...props}>
                <div className={`neo-loader-linear-bar neo-loader-linear-bar-${variant}`}></div>
            </div>
        );
    }

    // Determina as classes do spinner circular
    const spinnerClass = [
        "neo-loader-spinner",
        `neo-loader-spinner-${size}`,
        `neo-loader-spinner-${variant}`
    ].join(" ");

    // Conteúdo do Spinner circular em SVG para traço suave de alta fidelidade
    const renderSpinnerSvg = () => (
        <svg className={spinnerClass} viewBox="22 22 44 44" {...props}>
            <circle
                className="neo-loader-circle"
                cx="44"
                cy="44"
                r="20.2"
                fill="none"
                strokeWidth="3.6"
            />
        </svg>
    );

    // 3. Modo Inline (Apenas o Spinner circular)
    if (type === "inline") {
        return (
            <div className={`neo-loader-inline-wrapper ${className}`} style={style}>
                {renderSpinnerSvg()}
                {label && <span className="neo-loader-label font-size-13 ms-2">{label}</span>}
            </div>
        );
    }

    // 4. Modo Overlay (Contêiner com Blur e Spinner circular)
    if (type === "overlay") {
        return (
            <div className={`neo-loader-overlay ${className}`} style={style}>
                <div className="text-center d-flex flex-column align-items-center">
                    {renderSpinnerSvg()}
                    {label && <span className="neo-loader-label font-size-13 mt-3">{label}</span>}
                </div>
            </div>
        );
    }

    // 5. Modo Full-Page (Capa de entrada)
    return (
        <div className={`neo-loader-full-page ${className}`} style={style}>
            <div className="text-center d-flex flex-column align-items-center">
                {/* Logo institucional pulsando em harmonia outonal */}
                <div className="neo-loader-logo-wrapper">
                    <img src={logoImg} alt="NeoApp" className="neo-loader-logo" />
                </div>
                {label && <span className="neo-loader-label font-size-14 mt-4">{label}</span>}
            </div>
        </div>
    );
};

NeoLoader.propTypes = {
    type: PropTypes.oneOf(["inline", "overlay", "full-page", "dots", "linear"]),
    size: PropTypes.oneOf(["sm", "md", "lg"]),
    variant: PropTypes.oneOf(["accent", "primary", "white"]),
    label: PropTypes.string,
    loading: PropTypes.bool,
    className: PropTypes.string,
    style: PropTypes.object
};

export default NeoLoader;

